import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  formatBucketLabel,
  type Granularity,
} from '../../../../shared/lib/analytics-format'
import type { AnalyticsSeriesPoint } from '../../../../shared/types/analytics'
import { useTranslation } from 'react-i18next'

interface ClicksTimeSeriesProps {
  data: AnalyticsSeriesPoint[]
  granularity: Granularity
  title?: string
}

const TOTAL_COLOR = '#3B82F6'
const UNIQUE_COLOR = '#10B981'

/** 기간 내 클릭/고유 클릭 추이 */
export default function ClicksTimeSeries({
  data,
  granularity,
  title,
}: ClicksTimeSeriesProps) {
  const { t } = useTranslation()
  const hasData = data.some(point => point.clicks > 0)

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-4 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          {title ?? t('analyticsPage.clicksOverTime')}
        </h3>
        {!hasData && <span className="text-xs text-gray-400">데이터 없음</span>}
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <AreaChart
          data={data}
          margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
        >
          <defs>
            <linearGradient id="totalClicks" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={TOTAL_COLOR} stopOpacity={0.35} />
              <stop offset="100%" stopColor={TOTAL_COLOR} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="uniqueClicks" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={UNIQUE_COLOR} stopOpacity={0.3} />
              <stop offset="100%" stopColor={UNIQUE_COLOR} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tickFormatter={value => formatBucketLabel(value, granularity)}
            tick={{ fontSize: 12 }}
            stroke="currentColor"
            className="text-gray-500 dark:text-gray-400"
            minTickGap={24}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke="currentColor"
            className="text-gray-500 dark:text-gray-400"
            allowDecimals={false}
            width={44}
          />
          <Tooltip
            labelFormatter={value =>
              formatBucketLabel(String(value), granularity)
            }
            contentStyle={{
              borderRadius: '12px',
              border: '1px solid rgb(229 231 235)',
              fontSize: '12px',
            }}
          />
          <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
          <Area
            type="monotone"
            dataKey="clicks"
            stroke={TOTAL_COLOR}
            strokeWidth={2}
            fill="url(#totalClicks)"
            name={t('analyticsPage.totalClicks')}
          />
          <Area
            type="monotone"
            dataKey="uniqueClicks"
            stroke={UNIQUE_COLOR}
            strokeWidth={2}
            fill="url(#uniqueClicks)"
            name={t('analyticsPage.uniqueVisitors')}
          />
        </AreaChart>
      </ResponsiveContainer>
    </section>
  )
}
